import { Config } from '../../../../context';
import { BootstrapError } from './bootstrapError';
import { appendSvgSprites, createSvgSpritesConainer } from './utils';
import { initI18n } from '@shared/lib/Bootstrap/initI18n';
import { clearSvgCacheOnce } from '@shared/lib/svgCache';
import WebskyAnalyticsApollo from '../../../../DataLayerAnalytics/WebskyAnalyticsApollo';

const loadScript = (url: string) =>
	new Promise<void>((resolve, reject) => {
		const script = document.createElement('script');
		script.src = url;
		script.type = 'text/javascript';
		script.async = true;

		script.onload = () => resolve(null);

		script.onerror = () => {
			reject(new BootstrapError(`Script load error for: ${url}`));
		};

		document.body.appendChild(script);
	});

const loadSvgSprite = async (url: string) => {
	try {
		const response = await fetch(url);
		const content = await response.text();

		return content;

		// вставляем svg в начало body
	} catch (e) {
		return new BootstrapError(`unable to load engine icons: ${url}`);
	}
};

// Основная абстрактная функция
// Можно расширять под любые типы внешних ресурсов — например, CSS, JSON и т.д.
interface AddExternalResourcesOptions {
	scripts?: string[];
	svgs?: string[];
}

const addExternalResources = async (options: AddExternalResourcesOptions) => {
	const { scripts = [], svgs = [] } = options;
	// Параллельно загружаем все скрипты
	if (scripts.length > 0) {
		await Promise.all(
			scripts.map(async scriptUrl => {
				await loadScript(scriptUrl);
			})
		);
	}

	if (svgs.length > 0) {
		const svgsLoadResults = await Promise.all(
			svgs.map(async svgUrl => {
				return await loadSvgSprite(svgUrl);
			})
		);

		appendSvgSprites(svgsLoadResults.filter(result => !(result instanceof BootstrapError)) as string[]);

		const svgsLoadErrors = svgsLoadResults.filter(result => result instanceof BootstrapError) as Error[];
		if (svgsLoadErrors.length > 0) {
			throw new BootstrapError(svgsLoadErrors.map((error: Error) => error.message).join('\n'));
		}
	}
};

// Функция инициализации приложения
export const bootstrap = async (config: Config) => {
	clearSvgCacheOnce();

	await initI18n(config?.global?.locale);

	if (document.body.querySelector('svg#svgSpritesRoot')) {
		return;
	}

	createSvgSpritesConainer();

	try {
		await addExternalResources({
			scripts: [
				...new Set([
					...(config.global.additionalResources?.scripts ?? []),
					...(config.global.additionalResources?.extraScripts ?? [])
				])
			]
		});

		console.info('All external scripts were loaded successfully.');
	} catch (BootstrapError) {
		console.error(BootstrapError);
	}

	try {
		await addExternalResources({
			svgs: [
				...new Set([
					...(config.global?.additionalResources?.svgs ?? []),
					...(config.global?.additionalResources?.extraSvgs ?? [])
				])
			]
		});

		console.info('All external svg sprites were loaded successfully.');
	} catch (BootstrapError) {
		console.error(BootstrapError);
	}

	try {
		WebskyAnalyticsApollo.setUsePixel(config.global.usePixel);
	} catch (e) {
		throw new BootstrapError(`${WebskyAnalyticsApollo.name} configuration error`);
	}
};
